import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import Spinner from '../components/Spinner';
import './DetailPages.css';
import './Team.css';

function AccountDetail({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [team, setTeam] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadUser(),
        loadTenant()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadUser = async () => {
    try {
      const users = await api.get('/users', tenantId);
      const foundUser = Array.isArray(users) ? users.find(u => u.id === id) : null;
      if (foundUser) {
        setUser(foundUser);
        if (foundUser.teamId) {
          loadTeam(foundUser.teamId);
        }
        if (foundUser.roleId) {
          loadRole(foundUser.roleId);
        }
      } else {
        showNotification('Benutzer nicht gefunden', 'error');
        navigate('/team');
      }
    } catch (error) {
      console.error('Error loading user:', error);
      showNotification('Fehler beim Laden des Benutzers', 'error');
      navigate('/team');
    }
  };

  const loadTenant = async () => {
    try {
      const tenants = await api.get('/tenants', null);
      const currentTenant = tenants.find(t => t.id === tenantId) || tenants[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const loadTeam = async (teamId) => {
    try {
      const teams = await api.get('/teams', tenantId);
      const foundTeam = Array.isArray(teams) ? teams.find(t => t.id === teamId) : null;
      setTeam(foundTeam);
    } catch (error) {
      console.error('Error loading team:', error);
    }
  };

  const loadRole = async (roleId) => {
    try {
      const roles = await api.get('/roles', tenantId);
      const foundRole = Array.isArray(roles) ? roles.find(r => r.id === roleId) : null;
      setRole(foundRole);
    } catch (error) {
      console.error('Error loading role:', error);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Admin',
      tenantAdmin: 'Tenant Admin',
      user: 'Benutzer'
    };
    return labels[role] || role;
  };

  if (loading) {
    return <div className="account-detail"><Spinner /></div>;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="account-detail">
      <div className="detail-header">
        <h1>Benutzerdetails</h1>
        <div className="detail-actions">
          <button onClick={() => navigate('/team')} className="btn-secondary">
            Zurück zum Team
          </button>
        </div>
      </div>

      <div className="detail-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', marginBottom: '2rem' }}>
          {user.avatar ? (
            <img 
              src={user.avatar} 
              alt={user.username} 
              style={{ 
                width: '120px', 
                height: '120px', 
                objectFit: 'cover', 
                borderRadius: '50%', 
                border: '2px solid #ddd' 
              }}
            />
          ) : (
            <div 
              className="avatar-placeholder"
              style={{ 
                width: '120px', 
                height: '120px', 
                fontSize: '3rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {user.username.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h2 style={{ margin: 0, fontSize: '1.8em' }}>{user.username}</h2>
            <p style={{ margin: '0.5rem 0', color: '#666' }}>
              {getRoleLabel(user.role)}
            </p>
          </div>
        </div>

        <table>
          <tbody>
            <tr>
              <th>Benutzername</th>
              <td>{user.username}</td>
            </tr>
            <tr>
              <th>Rolle</th>
              <td>{getRoleLabel(user.role)}</td>
            </tr>
            {role && (
              <tr>
                <th>Zugewiesene Rolle</th>
                <td>{role.name}</td>
              </tr>
            )}
            {team && (
              <tr>
                <th>Team</th>
                <td>{team.name}</td>
              </tr>
            )}
            {tenant && (
              <tr>
                <th>Tenant</th>
                <td>{tenant.name}</td>
              </tr>
            )}
            {user.email && (
              <tr>
                <th>E-Mail</th>
                <td>{user.email}</td>
              </tr>
            )}
            {user.emailVerified !== undefined && (
              <tr>
                <th>E-Mail bestätigt</th>
                <td>{user.emailVerified ? 'Ja' : 'Nein'}</td>
              </tr>
            )}
            {user.createdAt && (
              <tr>
                <th>Erstellt am</th>
                <td>{new Date(user.createdAt).toLocaleDateString('de-DE')}</td>
              </tr>
            )}
            {user.updatedAt && (
              <tr>
                <th>Zuletzt aktualisiert</th>
                <td>{new Date(user.updatedAt).toLocaleDateString('de-DE')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AccountDetail;
